// The dropzone switcher: a menu that NAVIGATES between dropzone hosts.
//
// Each dropzone is a separate deployment — its own frontend host, its own
// backend, its own database. Switching sends the browser to that field's own
// host. It does NOT re-point this build's API.
//
// That distinction is the whole design:
//
//   * The URL always names the field you are looking at. An operations screen
//     showing South Shore's loads while the address bar reads
//     parachutemontreal is how someone manifests a jumper at the wrong field.
//   * Separate origins mean the browser partitions localStorage for us, so a
//     session at one field can never leak into another and there are no keys
//     to namespace by hand.
//   * REACT_APP_BACKEND_URL stays authoritative per build, so no build can
//     ever talk to another field's backend. That failure mode simply does not
//     exist any more.
//
// REACT_APP_DROPZONES is a JSON array baked at build time:
//
//   [{"slug":"northshore","name":"North Shore",
//     "web":"https://northshore.parachutemontreal.ultimatedzm.com"}]
//
// Absent or malformed = no switcher, which is every deployment that is not
// part of a group (dev, demo, and the ultimatedzm marketing build).

function parseDropzones() {
  const raw = process.env.REACT_APP_DROPZONES;
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (d) => d && typeof d.slug === 'string' && typeof d.web === 'string' && typeof d.name === 'string'
    );
  } catch {
    // A malformed value must not throw at import time and white-screen every
    // page. Degrading to "no switcher" is a far smaller failure.
    return [];
  }
}

export const DROPZONES = parseDropzones();

/** True when this build belongs to a group and should show the switcher. */
export const hasSwitcher = () => DROPZONES.length > 1;

const normalise = (url) => String(url || '').replace(/\/+$/, '').toLowerCase();

/**
 * Which dropzone this page IS, decided by the origin actually being served —
 * not by anything remembered. There is nothing to remember: the host is the
 * answer, so a stale preference cannot disagree with what is on screen.
 *
 * Null when the current origin is not in the list (a preview URL, localhost,
 * the *.cloudfront.net name). The switcher then still lists the fields; it
 * simply has nothing to mark as current.
 */
export function currentDropzone() {
  if (DROPZONES.length === 0) return null;
  const here = normalise(window.location.origin);
  return DROPZONES.find((d) => normalise(d.web) === here) || null;
}

export const findDropzone = (slug) => DROPZONES.find((d) => d.slug === slug) || null;

/**
 * Go to another dropzone.
 *
 * A full navigation to a different origin, so nothing of this field survives:
 * not React Query's cache of its customers, loads and money, not its tokens,
 * not any module-level state. The browser gives us that isolation for free and
 * more completely than any cache-clearing we could write.
 *
 * `path` is where to land on the other host. Callers pass a path that exists
 * there — never the current URL blindly, since a deep link like
 * /app/customers/<id> would point at an id from the wrong database.
 */
export function switchDropzone(slug, { path = '/app/dashboard' } = {}) {
  const dz = findDropzone(slug);
  if (!dz) return false;
  window.location.assign(normalise(dz.web) + path);
  return true;
}

// ── Group host ─────────────────────────────────────────────────────────────
//
// The group host is the one build that sits ABOVE the fields rather than being
// one of them. Its backend carries the group console; its own dropzone
// database is not an operating field, which is why the per-field Admin screen
// is hidden there — configuring "this dropzone" from the group host means
// configuring a field nobody flies at.
//
// Baked at build time, exactly like REACT_APP_DROPZONES, and false everywhere
// else by omission.
export const isGroupHost = () => process.env.REACT_APP_GROUP_HOST === 'true';
